/**
 * Text Effects Engine (template_engine / editor preview)
 * ======================================================
 *
 * Computes always-on visual styling for a text layer: shadows, strokes, glows,
 * gradients, materials, distortions, textures, masks/compositing, animated
 * overlays. 78 presets across 10 categories.
 *
 * Mirror of video_worker/py_server/app/utils/text_effects.py — keep the effect
 * ids, params and composition order IDENTICAL so the editor preview matches the
 * rendered video. See e:\code\TEXT_EFFECTS_CONTRACT.md for the shared contract.
 *
 * Attaches via item.textEffects = [{ type, enabled, ...params }, ...] (an ARRAY,
 * applied in order). This is separate from item.animations (time-based) and
 * item.motion (base transform); all three compose.
 *
 * Entry points:
 *   computeTextEffects(effects, item, t, ctx) -> TextEffectResult  (renderer-neutral)
 *   textEffectsToDomStyles(result, item, ctx) -> { style, layers, fillStyle }  (DOM)
 */

import { ease } from './motionEngine'

// ---------------------------------------------------------------------------
// Core helpers
// ---------------------------------------------------------------------------

const clamp01 = (v) => Math.max(0, Math.min(1, v))
const num = (v, d) => (typeof v === 'number' && !Number.isNaN(v) ? v : d)

/** Deterministic pseudo-random in [-1,1] — SAME constant as motion/anim engines. */
export const fxNoise = (step, seed) => {
  const x = Math.sin((step + 1) * 127.1 + (seed || 0) * 311.7) * 43758.5453
  const frac = x - Math.floor(x)
  return frac * 2 - 1
}

/** Deterministic pseudo-random in [0,1]. */
const fxRand = (step, seed) => (fxNoise(step, seed) + 1) / 2

/** Parse a #hex / rgb() / named color into {r,g,b,a}. Falls back to black. */
export const parseColor = (c) => {
  if (!c || typeof c !== 'string') return { r: 0, g: 0, b: 0, a: 1 }
  const s = c.trim()
  if (s[0] === '#') {
    let hex = s.slice(1)
    if (hex.length === 3) hex = hex.split('').map((h) => h + h).join('')
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1
      return { r, g, b, a }
    }
  }
  const m = s.match(/rgba?\(([^)]+)\)/)
  if (m) {
    const parts = m[1].split(',').map((p) => parseFloat(p.trim()))
    return { r: parts[0] || 0, g: parts[1] || 0, b: parts[2] || 0, a: parts[3] != null ? parts[3] : 1 }
  }
  // A few common named colors (most inputs are hex in this app).
  const named = { white: '#ffffff', black: '#000000', red: '#ff0000', green: '#00ff00', blue: '#0000ff', transparent: '#00000000' }
  if (named[s.toLowerCase()]) return parseColor(named[s.toLowerCase()])
  return { r: 0, g: 0, b: 0, a: 1 }
}

export const rgba = (c, a) => {
  const o = typeof c === 'string' ? parseColor(c) : c
  const alpha = a != null ? a : (o.a != null ? o.a : 1)
  return `rgba(${Math.round(o.r)}, ${Math.round(o.g)}, ${Math.round(o.b)}, ${clamp01(alpha)})`
}

/** Elapsed seconds since the effect/item window start (for animated effects). */
const elapsed = (cfg, t, ctx) => {
  const start = (ctx?.time?.start || 0) + Math.max(0, cfg.start || 0)
  return Math.max(0, (t || 0) - start)
}

/** A looping 0-1 phase at the given speed (cycles/sec). */
const loopPhase = (cfg, t, ctx, speed) => {
  const spd = speed != null ? speed : 1
  const e = elapsed(cfg, t, ctx)
  return (e * spd) % 1
}

// ---------------------------------------------------------------------------
// Result builder
// ---------------------------------------------------------------------------

const emptyResult = () => ({
  box: null,
  behind: [],
  fill: null,
  strokes: [],
  over: [],
  lines: [],
  filters: {},
  animated: false,
})

// ---------------------------------------------------------------------------
// Effect registry — each handler mutates the shared result for one effect.
// Signature: (cfg, res, item, t, ctx, h) where h = helpers bundle.
// ---------------------------------------------------------------------------

const EFFECTS = {}
const registerEffect = (type, fn) => { EFFECTS[type] = fn }
export const isTextEffectType = (type) => Object.prototype.hasOwnProperty.call(EFFECTS, type)
export const textEffectTypes = () => Object.keys(EFFECTS)

const H = { clamp01, num, parseColor, rgba, fxNoise, fxRand, elapsed, loopPhase, ease }

// ===========================================================================
// Category 1 — Basic Styling
// ===========================================================================

// 1. Drop Shadow
registerEffect('dropShadow', (cfg, res, item, t, ctx) => {
  const angle = cfg.angle != null ? cfg.angle : null
  const distance = cfg.distance != null ? cfg.distance : null
  let ox = num(cfg.offsetX, 4)
  let oy = num(cfg.offsetY, 4)
  if (angle != null && distance != null) {
    const a = (angle * Math.PI) / 180
    ox = Math.cos(a) * distance
    oy = Math.sin(a) * distance
  }
  res.behind.push({
    kind: 'shadow', color: cfg.color || '#000000', opacity: num(cfg.opacity, 0.6),
    offsetX: ox, offsetY: oy, blur: num(cfg.blur, 6), spread: num(cfg.spread, 0),
  })
})

// 2. Stroke / Outline
registerEffect('stroke', (cfg, res) => {
  res.strokes.push({
    color: cfg.color || '#ffffff', width: num(cfg.width, 3),
    align: cfg.align || 'outside', opacity: num(cfg.opacity, 1), join: cfg.join || 'round',
  })
})

// 3. Inner Shadow
registerEffect('innerShadow', (cfg, res) => {
  const angle = num(cfg.angle, 135)
  const depth = num(cfg.depth, 4)
  const a = (angle * Math.PI) / 180
  res.over.push({
    kind: 'innerShadow', clip: true, color: cfg.color || '#000000', opacity: num(cfg.opacity, 0.6),
    offsetX: cfg.offsetX != null ? cfg.offsetX : Math.cos(a) * depth,
    offsetY: cfg.offsetY != null ? cfg.offsetY : Math.sin(a) * depth,
    blur: num(cfg.blur, 4),
  })
})

// 4. Inner Glow
registerEffect('innerGlow', (cfg, res) => {
  res.over.push({
    kind: 'innerGlow', clip: true, color: cfg.color || '#ffffff', intensity: num(cfg.intensity, 0.8),
    radius: num(cfg.radius, 8), opacity: num(cfg.opacity, 0.9), edge: num(cfg.edge, 4), blend: cfg.blend || 'screen',
  })
})

// 5. Background Box
registerEffect('backgroundBox', (cfg, res) => {
  res.box = {
    color: cfg.color || '#000000', opacity: num(cfg.opacity, 0.5), radius: num(cfg.radius, 6),
    padding: num(cfg.padding, 10), border: num(cfg.border, 0), borderColor: cfg.borderColor || '#ffffff',
    gradient: cfg.gradient || null, align: cfg.align || 'center',
  }
})

// 6. Text Fill
registerEffect('textFill', (cfg, res) => {
  res.fill = {
    mode: cfg.mode || 'solid', color: cfg.color || '#000000', gradient: cfg.gradient || null,
    image: cfg.image || null, opacity: num(cfg.opacity, 1), blend: cfg.blend || 'normal',
    scale: num(cfg.scale, 1), offset: cfg.offset || { x: 0, y: 0 },
  }
})

// 7. Long Shadow
registerEffect('longShadow', (cfg, res) => {
  const angle = num(cfg.angle, 45)
  const length = num(cfg.length, 30)
  res.behind.push({
    kind: 'longshadow', color: cfg.color || '#000000', opacity: num(cfg.opacity, 0.4),
    angle, length, mode: cfg.mode || 'solid',
  })
})

// 8. Multiple Outlines
registerEffect('multipleOutlines', (cfg, res) => {
  const outlines = Array.isArray(cfg.outlines) && cfg.outlines.length
    ? cfg.outlines
    : [{ color: '#000000', width: 6 }, { color: '#ff0000', width: 10 }]
  // Widest first (outermost), so push accumulated widths.
  const sorted = [...outlines].sort((a, b) => num(b.width, 0) - num(a.width, 0))
  sorted.forEach((o) => {
    res.strokes.push({ color: o.color || '#000000', width: num(o.width, 4), align: 'outside', opacity: num(o.opacity, 1), join: cfg.join || 'round' })
  })
})

// 9. Double Shadow
registerEffect('doubleShadow', (cfg, res) => {
  const near = cfg.near || { color: '#000000', offsetX: 2, offsetY: 2, blur: 2, opacity: 0.7 }
  const far = cfg.far || { color: '#000000', offsetX: 10, offsetY: 12, blur: 14, opacity: 0.4 }
  // Far first (further behind), then near.
  res.behind.push({ kind: 'shadow', color: far.color || '#000000', opacity: num(far.opacity, 0.4), offsetX: num(far.offsetX, 10), offsetY: num(far.offsetY, 12), blur: num(far.blur, 14), spread: num(far.spread, 0) })
  res.behind.push({ kind: 'shadow', color: near.color || '#000000', opacity: num(near.opacity, 0.7), offsetX: num(near.offsetX, 2), offsetY: num(near.offsetY, 2), blur: num(near.blur, 2), spread: num(near.spread, 0) })
})

// 10. Underline & Overline
registerEffect('underlineOverline', (cfg, res) => {
  const lines = Array.isArray(cfg.lines) && cfg.lines.length
    ? cfg.lines
    : [{ position: 'under', color: cfg.color || '#000000', thickness: 3, offset: 2 }]
  lines.forEach((l) => res.lines.push({
    position: l.position || 'under', color: l.color || '#000000', thickness: num(l.thickness, 3),
    offset: num(l.offset, 2), lengthPct: num(l.lengthPct, 100), cap: l.cap || 'butt',
  }))
})

// ===========================================================================
// Category 2 — Shadows / Depth / 3D
// ===========================================================================

// 11. Outer Glow
registerEffect('outerGlow', (cfg, res) => {
  const layers = Math.max(1, Math.round(num(cfg.layers, 2)))
  for (let i = 0; i < layers; i++) {
    const f = (i + 1) / layers
    res.behind.push({
      kind: 'glow', color: cfg.color || '#66ccff', blur: num(cfg.radius, 12) * (0.5 + f),
      opacity: num(cfg.opacity, 0.9) * (1 - i * 0.25), spread: num(cfg.spread, 2), intensity: num(cfg.intensity, 1), blend: cfg.blend || 'screen',
    })
  }
})

// 12. Neon Glow
registerEffect('neonGlow', (cfg, res, item, t, ctx, h) => {
  const core = cfg.coreColor || '#ff2d95'
  let bright = num(cfg.brightness, 1)
  const flicker = num(cfg.flicker, 0)
  if (flicker > 0) {
    res.animated = true
    const n = h.fxNoise(Math.floor(h.elapsed(cfg, t, ctx) * 14), cfg.seed || 1)
    bright *= 1 - flicker * Math.max(0, n) * 0.5
  }
  // Outer halo (behind) + bright inner core stroke + inner glow (over).
  res.behind.push({ kind: 'glow', color: cfg.outerGlow || core, blur: num(cfg.halo, 24), opacity: 0.9 * bright, intensity: 1, blend: 'screen' })
  res.behind.push({ kind: 'glow', color: cfg.outerGlow || core, blur: num(cfg.halo, 24) * 0.5, opacity: 0.9 * bright, intensity: 1, blend: 'screen' })
  res.strokes.push({ color: '#ffffff', width: num(cfg.tubeWidth, 2), align: 'center', opacity: 0.9 * bright, join: 'round' })
  res.over.push({ kind: 'innerGlow', clip: true, color: cfg.innerGlow || core, intensity: 0.9, radius: 6, opacity: 0.8 * bright, edge: 3, blend: 'screen' })
})

// 13. Bevel & Emboss
registerEffect('bevelEmboss', (cfg, res) => {
  const a = (num(cfg.lightAngle, 135) * Math.PI) / 180
  const d = num(cfg.depth, 3)
  res.over.push({
    kind: 'bevel', clip: true, mode: cfg.mode || 'inner', width: num(cfg.width, 4), depth: d,
    highlight: cfg.highlight || '#ffffff', shadow: cfg.shadow || '#000000', softness: num(cfg.softness, 2),
    dx: Math.cos(a) * d, dy: Math.sin(a) * d,
  })
})

// 14. 3D Extrusion
registerEffect('extrude3D', (cfg, res) => {
  const a = (num(cfg.direction, 45) * Math.PI) / 180
  res.behind.push({
    kind: 'extrude', depth: num(cfg.depth, 12), dx: Math.cos(a), dy: Math.sin(a),
    sideColor: cfg.sideColor || '#555555', shading: num(cfg.shading, 1), softness: num(cfg.edgeSoftness, 0),
  })
  if (cfg.frontColor) res.fill = { mode: 'solid', color: cfg.frontColor, opacity: 1, blend: 'normal' }
})

// 15. Metallic Finish
registerEffect('metallic', (cfg, res) => {
  const palettes = {
    gold: ['#8a6a1f', '#ffe9a3', '#c9972b', '#fff4cf', '#9c7a24'],
    silver: ['#6b6b6b', '#ffffff', '#9a9a9a', '#e8e8e8', '#7a7a7a'],
    bronze: ['#5c3a1a', '#e0a878', '#8a5a2b', '#f2c79b', '#6b451f'],
    steel: ['#3a4452', '#cfd8e3', '#5a6775', '#eaf0f7', '#454f5c'],
  }
  const pal = Array.isArray(cfg.palette) && cfg.palette.length ? cfg.palette : (palettes[cfg.metal] || palettes.gold)
  const stops = pal.map((c, i) => ({ color: c, position: (i / (pal.length - 1)) * 100 }))
  res.fill = { mode: 'linear', gradient: { type: 'linear', angle: 90, stops }, opacity: 1, blend: 'normal', reflectivity: num(cfg.reflectivity, 0.8) }
  if (num(cfg.bevel, 0) > 0) res.over.push({ kind: 'bevel', clip: true, mode: 'inner', width: num(cfg.bevel, 2), depth: 2, highlight: '#ffffff', shadow: '#000000', softness: 1, dx: -2, dy: -2 })
})

// 16. Glass / Frosted Glass
registerEffect('glass', (cfg, res) => {
  res.filters.bgBlur = num(cfg.bgBlur, 6)
  res.fill = { mode: 'solid', color: cfg.tint || '#ffffff', opacity: num(cfg.transparency, 0.25), blend: 'screen' }
  res.over.push({ kind: 'edgeGlow', clip: true, color: cfg.edgeHighlight || '#ffffff', edgeWidth: 2, brightness: 1, blur: 1, mode: 'inner', blend: 'screen' })
  res._bgComposite = true
})

// 17. Reflection
registerEffect('reflection', (cfg, res) => {
  res.over.push({
    kind: 'reflection', clip: false, direction: cfg.direction || 'down', distance: num(cfg.distance, 4),
    opacity: num(cfg.opacity, 0.4), fade: num(cfg.fade, 0.6), blur: num(cfg.blur, 1), flip: cfg.flip !== false,
  })
})

// 18. Extruded Outline
registerEffect('extrudedOutline', (cfg, res) => {
  const a = (num(cfg.direction, 45) * Math.PI) / 180
  res.behind.push({ kind: 'extrude', depth: num(cfg.depth, 10), dx: Math.cos(a), dy: Math.sin(a), sideColor: cfg.sideColor || '#333333', shading: num(cfg.shading, 1), softness: 0, outline: num(cfg.outlineWidth, 6) })
  res.strokes.push({ color: cfg.frontColor || '#ffffff', width: num(cfg.outlineWidth, 6), align: 'outside', opacity: 1, join: 'round' })
})

// ===========================================================================
// Category 3 — Lighting / Highlights
// ===========================================================================

// 19. Light Sweep
registerEffect('lightSweep', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const pos = h.loopPhase(cfg, t, ctx, num(cfg.speed, 0.4))
  res.over.push({
    kind: 'lightSweep', clip: true, color: cfg.color || '#ffffff', width: num(cfg.width, 0.25),
    angle: num(cfg.angle, 20), brightness: num(cfg.brightness, 0.9), feather: num(cfg.feather, 0.5),
    position: pos, blend: cfg.blend || 'screen',
  })
})

// 20. Lens Flare
registerEffect('lensFlare', (cfg, res) => {
  res.over.push({
    kind: 'lensFlare', clip: false, x: num(cfg.x, 0.8), y: num(cfg.y, 0.3), brightness: num(cfg.brightness, 1),
    color: cfg.color || '#ffffff', size: num(cfg.size, 0.4), streak: num(cfg.streak, 0.6), opacity: num(cfg.opacity, 0.9),
  })
})

// 21. Edge Glow
registerEffect('edgeGlow', (cfg, res) => {
  res.over.push({
    kind: 'edgeGlow', clip: true, color: cfg.color || '#00e5ff', edgeWidth: num(cfg.edgeWidth, 3),
    brightness: num(cfg.brightness, 1), blur: num(cfg.blur, 3), mode: cfg.mode || 'inner', blend: cfg.blend || 'screen',
  })
})

// 22. Animated Glow
registerEffect('animatedGlow', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const e = h.elapsed(cfg, t, ctx)
  const speed = num(cfg.speed, 1)
  let pulse = (Math.sin(e * speed * Math.PI * 2) + 1) / 2
  if (num(cfg.flicker, 0) > 0) pulse *= 1 - cfg.flicker * Math.max(0, h.fxNoise(Math.floor(e * 12), cfg.seed || 1))
  const intensity = h.num(cfg.minIntensity, 0.4) + (h.num(cfg.maxIntensity, 1) - h.num(cfg.minIntensity, 0.4)) * pulse
  res.behind.push({ kind: 'glow', color: cfg.colorVar || cfg.color || '#66ccff', blur: num(cfg.radius, 16), opacity: intensity, intensity: 1, blend: 'screen' })
})

// 23. Sparkle & Glint
registerEffect('sparkle', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const count = Math.max(1, Math.round(num(cfg.count, 6)))
  const e = h.elapsed(cfg, t, ctx)
  const dur = num(cfg.duration, 1.2)
  const sparks = []
  for (let i = 0; i < count; i++) {
    const seed = (cfg.seed || 1) + i
    const px = h.fxRand(i * 3 + 1, seed)
    const py = h.fxRand(i * 3 + 2, seed)
    const birth = h.fxRand(i * 3 + 3, seed) * dur
    const localT = ((e - birth) % dur + dur) % dur
    const life = h.clamp01(localT / (dur * 0.5))
    const vis = life < 0.5 ? life * 2 : (1 - life) * 2
    sparks.push({ x: px, y: py, size: num(cfg.size, 6) * (0.5 + vis), opacity: h.clamp01(vis) * num(cfg.brightness, 1) })
  }
  res.over.push({ kind: 'sparkle', clip: true, color: cfg.color || '#ffffff', sparks })
})

// 24. Light Rays
registerEffect('lightRays', (cfg, res, item, t, ctx, h) => {
  const rot = num(cfg.rotation, 0) + (cfg.flicker ? h.loopPhase(cfg, t, ctx, 0.1) * 360 : 0)
  if (cfg.flicker) res.animated = true
  res.over.push({
    kind: 'lightRays', clip: false, x: num(cfg.x, 0.5), y: num(cfg.y, 0.5), length: num(cfg.length, 1),
    width: num(cfg.width, 0.5), intensity: num(cfg.intensity, 0.8), color: cfg.color || '#ffffff', rotation: rot,
  })
})

// ===========================================================================
// Category 4 — Gradients / Color
// ===========================================================================

const gradientStops = (cfg, fallback) => {
  if (Array.isArray(cfg.stops) && cfg.stops.length >= 2) return cfg.stops
  return fallback
}

// 25. Linear Gradient Fill
registerEffect('linearGradient', (cfg, res) => {
  const stops = gradientStops(cfg, [{ color: '#ff3b3b', position: 0 }, { color: '#ff9e3b', position: 100 }])
  res.fill = { mode: 'linear', gradient: { type: 'linear', angle: num(cfg.angle, 90), stops }, opacity: num(cfg.opacity, 1), blend: 'normal' }
})

// 26. Radial Gradient Fill
registerEffect('radialGradient', (cfg, res) => {
  const stops = gradientStops(cfg, [{ color: '#ffee88', position: 0 }, { color: '#d9731f', position: 100 }])
  res.fill = { mode: 'radial', gradient: { type: 'radial', centerX: num(cfg.centerX, 0.5), centerY: num(cfg.centerY, 0.5), radius: num(cfg.radius, 0.7), scaleX: num(cfg.scaleX, 1), scaleY: num(cfg.scaleY, 1), stops }, opacity: num(cfg.opacity, 1), blend: 'normal' }
})

// 27. Animated Gradient
registerEffect('animatedGradient', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const stops = gradientStops(cfg, [{ color: '#b31217', position: 0 }, { color: '#e52d27', position: 50 }, { color: '#ffd200', position: 100 }])
  const shift = h.loopPhase(cfg, t, ctx, num(cfg.speed, 0.3)) * 100
  const shifted = stops.map((s) => ({ color: s.color, position: (s.position + shift) % 100 }))
  res.fill = { mode: 'linear', gradient: { type: 'linear', angle: num(cfg.angle, 90), stops: shifted, scale: num(cfg.scale, 1) }, opacity: 1, blend: 'normal' }
})

// 28. Duotone
registerEffect('duotone', (cfg, res) => {
  res.fill = { mode: 'duotone', shadowColor: cfg.shadowColor || '#13294b', highlightColor: cfg.highlightColor || '#ff7a1a', contrast: num(cfg.contrast, 1), threshold: num(cfg.threshold, 0.5), opacity: 1, blend: cfg.blend || 'normal' }
})

// 29. Color Overlay
registerEffect('colorOverlay', (cfg, res) => {
  if ((cfg.scope || 'fill') === 'fill') {
    res.fill = { mode: 'solid', color: cfg.color || '#ff0000', opacity: num(cfg.opacity, 1), blend: cfg.blend || 'normal' }
  } else {
    res.over.push({ kind: 'overlay', clip: false, color: cfg.color || '#ff0000', opacity: num(cfg.opacity, 0.5), blend: cfg.blend || 'normal' })
  }
})

// 30. Color Shift
registerEffect('colorShift', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const stops = gradientStops(cfg, [{ color: '#ffffff', position: 0 }, { color: '#ff0000', position: 100 }])
  const dur = num(cfg.duration, 2)
  const loop = cfg.loop !== false
  let p = h.elapsed(cfg, t, ctx) / dur
  p = loop ? (p % 1) : h.clamp01(p)
  const eased = h.ease(p, cfg.easing || 'easeInOut')
  const col = sampleGradientColor(stops, eased * 100)
  res.fill = { mode: 'solid', color: col, opacity: 1, blend: 'normal' }
})

// 31. Duotone Texture Blend
registerEffect('duotoneTexture', (cfg, res) => {
  res.fill = { mode: 'duotone', texture: cfg.texture || null, shadowColor: cfg.shadowColor || '#3b2a1a', highlightColor: cfg.highlightColor || '#f5e6c8', contrast: num(cfg.contrast, 1), scale: num(cfg.scale, 1), opacity: 1, blend: cfg.blend || 'normal' }
})

/** Sample a color from stops ([{color,position 0-100}]) at position p (0-100). */
const sampleGradientColor = (stops, p) => {
  const sorted = [...stops].sort((a, b) => a.position - b.position)
  if (p <= sorted[0].position) return sorted[0].color
  if (p >= sorted[sorted.length - 1].position) return sorted[sorted.length - 1].color
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i]; const b = sorted[i + 1]
    if (p >= a.position && p <= b.position) {
      const f = (p - a.position) / Math.max(0.0001, b.position - a.position)
      const ca = parseColor(a.color); const cb = parseColor(b.color)
      const r = Math.round(ca.r + (cb.r - ca.r) * f)
      const g = Math.round(ca.g + (cb.g - ca.g) * f)
      const bl = Math.round(ca.b + (cb.b - ca.b) * f)
      return `rgb(${r}, ${g}, ${bl})`
    }
  }
  return sorted[0].color
}

// ===========================================================================
// Category 5 — Material / Surface  (texture-driven; DOM approximates w/ gradients)
// ===========================================================================

const materialFill = (res, cfg, texture, fallbackStops, angle) => {
  res.fill = {
    mode: 'linear', texture: texture || cfg.texture || null,
    gradient: { type: 'linear', angle: angle != null ? angle : 90, stops: fallbackStops },
    contrast: num(cfg.contrast, 1), scale: num(cfg.scale, 1), opacity: 1, blend: cfg.blend || 'normal',
  }
}

// 32. Stone / Marble
registerEffect('stoneMarble', (cfg, res) => {
  materialFill(res, cfg, 'stone', [{ color: '#6b6b6b', position: 0 }, { color: '#c9c9c9', position: 50 }, { color: '#4a4a4a', position: 100 }])
  if (num(cfg.bevel, 0) > 0) res.over.push({ kind: 'bevel', clip: true, mode: 'inner', width: num(cfg.bevel, 2), depth: 2, highlight: cfg.highlight || '#ffffff', shadow: cfg.shadow || '#000000', softness: 2, dx: -2, dy: -2 })
})

// 33. Wood Texture
registerEffect('woodTexture', (cfg, res) => {
  const angle = (cfg.grainDir === 'vertical') ? 0 : 90
  materialFill(res, cfg, 'wood', [{ color: '#6b3f1d', position: 0 }, { color: '#9c6b3a', position: 50 }, { color: '#5a3318', position: 100 }], angle)
  if (cfg.tint) res.over.push({ kind: 'overlay', clip: true, color: cfg.tint, opacity: 0.2, blend: 'multiply' })
})

// 34. Paper / Fabric
registerEffect('paperFabric', (cfg, res) => {
  materialFill(res, cfg, cfg.texture || 'paper', [{ color: '#efe7d6', position: 0 }, { color: '#d8ccb4', position: 100 }])
  res.fill.opacity = num(cfg.opacity, 1)
  if (num(cfg.edgeDistress, 0) > 0) res.filters.edgeDistort = { roughness: cfg.edgeDistress, strength: cfg.edgeDistress * 2, edgeWidth: 3, scale: 0.4 }
})

// 35. Holographic
registerEffect('holographic', (cfg, res, item, t, ctx, h) => {
  const pal = Array.isArray(cfg.palette) && cfg.palette.length ? cfg.palette : ['#ff00cc', '#3333ff', '#00ffee', '#ff00cc']
  let shift = 0
  if (cfg.animShift) { res.animated = true; shift = h.loopPhase(cfg, t, ctx, 0.25) * 100 }
  const stops = pal.map((c, i) => ({ color: c, position: (i / (pal.length - 1)) * 100 + shift }))
  res.fill = { mode: 'linear', gradient: { type: 'linear', angle: num(cfg.highlightDir, 60), stops }, opacity: 1, blend: 'normal' }
  res.over.push({ kind: 'lightSweep', clip: true, color: '#ffffff', width: 0.3, angle: 30, brightness: num(cfg.reflection, 0.6), feather: 0.6, position: cfg.animShift ? h.loopPhase(cfg, t, ctx, 0.2) : 0.5, blend: 'screen' })
})

// 36. Chrome
registerEffect('chrome', (cfg, res, item, t, ctx, h) => {
  const pal = Array.isArray(cfg.palette) && cfg.palette.length ? cfg.palette : ['#222a33', '#aeb9c6', '#ffffff', '#6a7682', '#1a2027']
  const stops = pal.map((c, i) => ({ color: c, position: (i / (pal.length - 1)) * 100 }))
  res.fill = { mode: 'linear', gradient: { type: 'linear', angle: 90, stops, contrast: num(cfg.contrast, 1.2) }, opacity: 1, blend: 'normal' }
  if (cfg.movingHighlight) { res.animated = true; res.over.push({ kind: 'lightSweep', clip: true, color: '#ffffff', width: num(cfg.bandWidth, 0.2), angle: 20, brightness: 1, feather: 0.4, position: h.loopPhase(cfg, t, ctx, 0.3), blend: 'screen' }) }
})

// 37. Liquid Metal
registerEffect('liquidMetal', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  res.fill = { mode: 'linear', gradient: { type: 'linear', angle: 90, stops: [{ color: '#6b6b6b', position: 0 }, { color: '#ffffff', position: 50 }, { color: '#8a8a8a', position: 100 }] }, opacity: 1, blend: 'normal' }
  res.filters.wave = { amplitude: num(cfg.distortion, 4), wavelength: num(cfg.rippleScale, 40), direction: 'horizontal', phase: h.loopPhase(cfg, t, ctx, num(cfg.animSpeed, 0.5)) * Math.PI * 2 }
})

// 38. Embossed Print
registerEffect('embossedPrint', (cfg, res) => {
  const a = (num(cfg.lightDir, 135) * Math.PI) / 180
  const d = num(cfg.depth, 3)
  res.over.push({ kind: 'bevel', clip: true, mode: 'emboss', width: num(cfg.softness, 2), depth: d, highlight: cfg.highlight || '#ffffff', shadow: cfg.shadow || '#000000', softness: num(cfg.softness, 2), dx: Math.cos(a) * d, dy: Math.sin(a) * d })
})

// ===========================================================================
// Category 6 — Distortion / Digital  (mostly whole-layer filters)
// ===========================================================================

// 39. Gaussian Blur
registerEffect('gaussianBlur', (cfg, res, item, t, ctx, h) => {
  let r = num(cfg.radius, 4)
  if (cfg.animated) { res.animated = true; r *= (Math.sin(h.elapsed(cfg, t, ctx) * Math.PI) + 1) / 2 }
  res.filters.blur = r
  if (cfg.blurX != null) res.filters.blurX = cfg.blurX
  if (cfg.blurY != null) res.filters.blurY = cfg.blurY
})

// 40. Directional Blur
registerEffect('directionalBlur', (cfg, res) => {
  res.filters.directionalBlur = { angle: num(cfg.angle, 0), distance: num(cfg.distance, 12), opacity: num(cfg.opacity, 1) }
})

// 41. Motion Blur Effect
registerEffect('motionBlurFx', (cfg, res) => {
  res.filters.motionBlur = { shutter: num(cfg.shutter, 180), samples: Math.max(2, Math.round(num(cfg.samples, 6))), intensity: num(cfg.intensity, 1) }
})

// 42. Glitch Distortion
registerEffect('glitchDistort', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const e = h.elapsed(cfg, t, ctx)
  const freq = num(cfg.burstFreq, 4)
  const burst = h.fxRand(Math.floor(e * freq), cfg.seed || 1) > 0.6 ? 1 : 0
  res.filters.glitch = {
    active: burst > 0, strength: num(cfg.strength, 8) * burst, blockSize: num(cfg.blockSize, 12),
    rgbSeparation: num(cfg.rgbSeparation, 4) * burst, noise: num(cfg.noise, 0.3), seed: cfg.seed || 1,
  }
})

// 43. RGB Split / Chromatic Aberration
registerEffect('rgbSplit', (cfg, res) => {
  res.filters.rgbSplit = {
    rOffset: cfg.rOffset != null ? cfg.rOffset : num(cfg.intensity, 3),
    gOffset: num(cfg.gOffset, 0), bOffset: cfg.bOffset != null ? cfg.bOffset : -num(cfg.intensity, 3),
    direction: cfg.direction || 'horizontal', blend: cfg.blend || 'screen',
  }
})

// 44. Displacement / Warp
registerEffect('displacement', (cfg, res, item, t, ctx, h) => {
  if (cfg.speed) res.animated = true
  res.filters.displacement = {
    strengthX: num(cfg.strengthX, 8), strengthY: num(cfg.strengthY, 8), scale: num(cfg.scale, 0.1),
    phase: cfg.speed ? h.loopPhase(cfg, t, ctx, cfg.speed) * Math.PI * 2 : 0, seed: cfg.seed || 1,
  }
})

// 45. Wave Distortion
registerEffect('waveDistort', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  res.filters.wave = {
    amplitude: num(cfg.amplitude, 8), wavelength: num(cfg.wavelength, 40), direction: cfg.direction || 'vertical',
    frequency: num(cfg.frequency, 1), phase: (num(cfg.phase, 0)) + h.loopPhase(cfg, t, ctx, num(cfg.speed, 0.5)) * Math.PI * 2,
  }
})

// 46. Pixelate
registerEffect('pixelate', (cfg, res) => {
  res.filters.pixelate = { size: Math.max(2, Math.round(num(cfg.size, 8))), smoothing: !!cfg.smoothing, colorDepth: num(cfg.colorDepth, 0) }
})

// 47. Noise / Static
registerEffect('noiseStatic', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  res.filters.noise = { amount: num(cfg.amount, 0.3), scale: num(cfg.scale, 1), mode: cfg.mode || 'mono', seed: (cfg.seed || 1) + Math.floor(h.elapsed(cfg, t, ctx) * num(cfg.speed, 12)) }
})

// 48. Edge Distortion
registerEffect('edgeDistort', (cfg, res, item, t, ctx, h) => {
  if (cfg.speed) res.animated = true
  res.filters.edgeDistort = { roughness: num(cfg.roughness, 0.5), strength: num(cfg.strength, 4), edgeWidth: num(cfg.edgeWidth, 3), scale: num(cfg.scale, 0.4), seed: (cfg.seed || 1) + (cfg.speed ? Math.floor(h.elapsed(cfg, t, ctx) * cfg.speed) : 0) }
})

// ===========================================================================
// Category 7 — Texture / Artistic
// ===========================================================================

// 49. Film Grain
registerEffect('filmGrain', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  res.filters.grain = { size: num(cfg.size, 1), intensity: num(cfg.intensity, 0.2), mono: cfg.mono !== false, contrast: num(cfg.contrast, 1), seed: (cfg.seed || 1) + Math.floor(h.elapsed(cfg, t, ctx) * num(cfg.speed, 24)) }
})

// 50. Halftone
registerEffect('halftone', (cfg, res) => {
  res.over.push({ kind: 'halftone', clip: true, dotSize: num(cfg.dotSize, 4), spacing: num(cfg.spacing, 6), angle: num(cfg.angle, 45), contrast: num(cfg.contrast, 1), palette: cfg.palette || ['#000000'], shape: cfg.shape || 'circle' })
})

// 51. Comic Book (compound)
registerEffect('comicBook', (cfg, res) => {
  res.strokes.push({ color: '#000000', width: num(cfg.outline, 4), align: 'outside', opacity: 1, join: 'round' })
  res.over.push({ kind: 'halftone', clip: true, dotSize: num(cfg.halftoneScale, 4), spacing: num(cfg.halftoneScale, 4) * 1.5, angle: 45, contrast: num(cfg.contrast, 1.2), palette: cfg.palette || ['#000000'], shape: 'circle' })
})

// 52. Distressed / Scratched
registerEffect('distressed', (cfg, res) => {
  res.filters.distress = { density: num(cfg.density, 0.3), size: num(cfg.size, 2), intensity: num(cfg.intensity, 0.6), edgeWear: num(cfg.edgeWear, 0.4), seed: cfg.seed || 1 }
})

// 53. Cracked
registerEffect('cracked', (cfg, res) => {
  res.over.push({ kind: 'cracked', clip: true, density: num(cfg.density, 0.4), width: num(cfg.width, 1), contrast: num(cfg.contrast, 1), glow: cfg.glow || null, seed: cfg.seed || 1 })
})

// 54. Burned / Charred
registerEffect('burnedCharred', (cfg, res, item, t, ctx) => {
  res.over.push({ kind: 'overlay', clip: true, color: '#1a1008', opacity: num(cfg.intensity, 0.5), blend: 'multiply' })
  res.over.push({ kind: 'edgeGlow', clip: true, color: cfg.edgeColor || '#ff5a1a', edgeWidth: 3, brightness: 1, blur: 2, mode: 'outer', blend: 'screen' })
  if (cfg.progress != null) { res.filters.burnAway = { progress: num(cfg.progress, 0), noiseScale: num(cfg.noiseScale, 0.3), edgeColor: cfg.edgeColor || '#ff5a1a', seed: cfg.seed || 1 }; res.animated = true }
})

// 55. Watercolor / Ink Bleed
registerEffect('watercolorInk', (cfg, res) => {
  res.filters.edgeDistort = { roughness: num(cfg.bleed, 0.5), strength: num(cfg.bleed, 0.5) * 6, edgeWidth: num(cfg.edgeSoftness, 4), scale: 0.3, seed: cfg.seed || 1 }
  if (cfg.colorVar) res.over.push({ kind: 'overlay', clip: true, color: cfg.colorVar, opacity: 0.2, blend: 'multiply' })
})

// 56. Paint / Brush
registerEffect('paintBrush', (cfg, res) => {
  materialFill(res, cfg, cfg.texture || 'brush', [{ color: '#222222', position: 0 }, { color: '#555555', position: 100 }], cfg.strokeDir === 'vertical' ? 0 : 90)
  res.filters.edgeDistort = { roughness: num(cfg.edgeRough, 0.4), strength: 4, edgeWidth: 3, scale: 0.4, seed: cfg.seed || 1 }
})

// ===========================================================================
// Category 8 — Masks / Compositing
// ===========================================================================

// 57. Alpha Mask
registerEffect('alphaMask', (cfg, res) => {
  res.filters.alphaMask = { source: cfg.source || null, invert: !!cfg.invert, feather: num(cfg.feather, 0), opacity: num(cfg.opacity, 1), x: num(cfg.x, 0), y: num(cfg.y, 0), scale: num(cfg.scale, 1) }
})

// 58. Luma Matte
registerEffect('lumaMatte', (cfg, res) => {
  res.filters.lumaMatte = { source: cfg.source || null, threshold: num(cfg.threshold, 0.5), contrast: num(cfg.contrast, 1), invert: !!cfg.invert, feather: num(cfg.feather, 0), scale: num(cfg.scale, 1) }
})

// 59. Blend Modes
registerEffect('blendMode', (cfg, res) => {
  res.filters.blendMode = { mode: cfg.mode || 'normal', opacity: num(cfg.opacity, 1) }
})

// 60. Background Blur Through Text
registerEffect('bgBlurThroughText', (cfg, res) => {
  res.filters.bgBlur = num(cfg.radius, 8)
  res._bgComposite = true
  res.fill = { mode: 'solid', color: cfg.tint || '#ffffff', opacity: num(cfg.textOpacity, 0.15), blend: 'screen' }
  if (cfg.edgeHighlight) res.over.push({ kind: 'edgeGlow', clip: true, color: '#ffffff', edgeWidth: 2, brightness: 1, blur: 1, mode: 'inner', blend: 'screen' })
})

// 61. Text Behind Subject
registerEffect('textBehindSubject', (cfg, res) => {
  res.filters.behindSubject = { subjectMask: cfg.subjectMask || null, refine: num(cfg.refine, 0), feather: num(cfg.feather, 1), x: num(cfg.x, 0), y: num(cfg.y, 0) }
})

// 62. Text Cutout
registerEffect('textCutout', (cfg, res) => {
  res.filters.cutout = { source: cfg.source || null, invert: !!cfg.invert, edgeSoftness: num(cfg.edgeSoftness, 0), outline: cfg.outline || null, fillLayer: cfg.fillLayer || null }
})

// ===========================================================================
// Category 9 — Animated Visual (overlays)
// ===========================================================================

const animOverlay = (res, kind, cfg, params) => {
  res.animated = true
  res.over.push({ kind, clip: cfg.mode === 'inside', ...params })
}

// 63. Fire Overlay
registerEffect('fireOverlay', (cfg, res, item, t, ctx, h) => {
  animOverlay(res, 'fire', cfg, { color: cfg.color || '#ff6a00', intensity: num(cfg.intensity, 1), speed: num(cfg.speed, 1), spread: num(cfg.spread, 1), glow: num(cfg.glow, 1), phase: h.elapsed(cfg, t, ctx), inside: cfg.mode === 'inside' })
})

// 64. Smoke Overlay
registerEffect('smokeOverlay', (cfg, res, item, t, ctx, h) => {
  animOverlay(res, 'smoke', cfg, { density: num(cfg.density, 0.6), direction: cfg.direction || 'up', speed: num(cfg.speed, 1), color: cfg.color || '#888888', opacity: num(cfg.opacity, 0.5), phase: h.elapsed(cfg, t, ctx), maskMode: cfg.maskMode || 'over' })
})

// 65. Sparks & Embers
registerEffect('sparksEmbers', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const count = Math.max(1, Math.round(num(cfg.count, 20)))
  const e = h.elapsed(cfg, t, ctx)
  const life = num(cfg.lifetime, 1.5)
  const parts = []
  for (let i = 0; i < count; i++) {
    const seed = (cfg.seed || 1) + i
    const birth = h.fxRand(i, seed) * life
    const lt = ((e - birth) % life + life) % life
    const lf = lt / life
    const x0 = h.fxRand(i * 2 + 1, seed)
    parts.push({ x: x0 + h.fxNoise(i, seed) * 0.05 * lt, y: 1 - lf, size: num(cfg.size, 2) * (1 - lf * 0.5), opacity: (1 - lf) * num(cfg.glow, 1) })
  }
  res.over.push({ kind: 'particles', clip: false, color: cfg.color || '#ffb24d', parts, glow: num(cfg.glow, 1) })
})

// 66. Lightning / Electric
registerEffect('lightning', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const flick = h.fxRand(Math.floor(h.elapsed(cfg, t, ctx) * num(cfg.flicker, 10)), cfg.seed || 1)
  res.over.push({ kind: 'lightning', clip: cfg.region === 'inside', color: cfg.color || '#8ad8ff', intensity: num(cfg.intensity, 1) * (flick > 0.5 ? 1 : 0.4), branches: Math.round(num(cfg.branches, 3)), glow: num(cfg.glow, 1), seed: (cfg.seed || 1) + Math.floor(h.elapsed(cfg, t, ctx) * 8) })
})

// 67. Water Ripple
registerEffect('waterRipple', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  res.filters.wave = { amplitude: num(cfg.amplitude, 6), wavelength: num(cfg.wavelength, 30), direction: cfg.direction || 'vertical', phase: h.loopPhase(cfg, t, ctx, num(cfg.speed, 0.6)) * Math.PI * 2 }
  if (num(cfg.reflection, 0) > 0) res.over.push({ kind: 'reflection', clip: false, direction: 'down', distance: 2, opacity: cfg.reflection, fade: 0.6, blur: 2, flip: true })
})

// 68. Heat Haze
registerEffect('heatHaze', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  res.filters.displacement = { strengthX: num(cfg.strength, 4), strengthY: num(cfg.strength, 4) * 1.5, scale: num(cfg.waveScale, 0.08), phase: h.loopPhase(cfg, t, ctx, num(cfg.speed, 0.4)) * Math.PI * 2, seed: cfg.seed || 1 }
})

// 69. Particle Trail
registerEffect('particleTrail', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  res.over.push({ kind: 'particleTrail', clip: false, rate: num(cfg.rate, 20), lifetime: num(cfg.lifetime, 1), trail: num(cfg.trail, 1), color: cfg.color || '#ffffff', glow: num(cfg.glow, 1), size: num(cfg.size, 3), phase: h.elapsed(cfg, t, ctx) })
})

// 70. Animated Outline Draw
registerEffect('animatedOutlineDraw', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const p = h.clamp01(h.elapsed(cfg, t, ctx) * num(cfg.speed, 0.5))
  res.strokes.push({ color: cfg.color || '#ffffff', width: num(cfg.width, 2), align: 'center', opacity: 1, join: 'round', drawProgress: p })
  if (num(cfg.glow, 0) > 0) res.over.push({ kind: 'edgeGlow', clip: true, color: cfg.color || '#ffffff', edgeWidth: 2, brightness: cfg.glow, blur: 3, mode: 'outer', blend: 'screen' })
  res.filters.outlineDrawReveal = { progress: p, fillReveal: num(cfg.fillReveal, 0.8) }
})

// ===========================================================================
// Category 10 — Advanced
// ===========================================================================

// 71. Liquid Distortion
registerEffect('liquidDistortion', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  res.filters.displacement = { strengthX: num(cfg.amount, 10), strengthY: num(cfg.amount, 10), scale: num(cfg.rippleScale, 0.1), phase: h.loopPhase(cfg, t, ctx, num(cfg.speed, 0.4)) * Math.PI * 2, seed: cfg.seed || 1, smooth: num(cfg.smoothness, 1) }
})

// 72. Morphing Text (simplified crossfade)
registerEffect('morphingText', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const p = h.clamp01(h.elapsed(cfg, t, ctx) / num(cfg.duration, 1))
  res.filters.morph = { targetText: cfg.targetText || '', progress: h.ease(p, cfg.easing || 'easeInOut'), distortion: num(cfg.distortion, 0) }
})

// 73. Fracture & Shatter
registerEffect('fractureShatter', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const p = h.clamp01(h.elapsed(cfg, t, ctx) / num(cfg.duration, 1.5))
  res.filters.fracture = { fragments: Math.round(num(cfg.fragments, 12)), size: num(cfg.size, 1), direction: num(cfg.direction, 90), force: num(cfg.force, 1), rotation: num(cfg.rotation, 1), gravity: num(cfg.gravity, 1), progress: p, seed: cfg.seed || 1 }
})

// 74. Burn Away
registerEffect('burnAway', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const p = cfg.progress != null ? num(cfg.progress, 0) : h.clamp01(h.elapsed(cfg, t, ctx) / num(cfg.duration, 2))
  res.filters.burnAway = { direction: cfg.direction || 'up', progress: p, edgeGlow: num(cfg.edgeGlow, 1), burnColor: cfg.burnColor || '#ff5a1a', noiseScale: num(cfg.noiseScale, 0.3), seed: cfg.seed || 1 }
})

// 75. Dissolve with Noise
registerEffect('dissolveNoise', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  const p = cfg.progress != null ? num(cfg.progress, 0) : h.clamp01(h.elapsed(cfg, t, ctx) / num(cfg.duration, 2))
  res.filters.dissolve = { noiseScale: num(cfg.noiseScale, 0.3), direction: cfg.direction || 'none', progress: p, edgeSoftness: num(cfg.edgeSoftness, 0.05), edgeGlow: cfg.edgeGlow || null, fragmentSize: num(cfg.fragmentSize, 3), seed: cfg.seed || 1 }
})

// 76. Animated Texture Fill
registerEffect('animatedTextureFill', (cfg, res, item, t, ctx, h) => {
  res.animated = true
  res.fill = { mode: 'image', image: cfg.source || null, animated: true, scale: num(cfg.scale, 1), offset: cfg.offset || { x: 0, y: 0 }, speed: num(cfg.speed, 1), blend: cfg.blend || 'normal', tint: cfg.tint || null, fit: cfg.fit || 'cover', phase: h.elapsed(cfg, t, ctx) * num(cfg.speed, 1) }
})

// 77. Edge Extrude + Lighting
registerEffect('edgeExtrudeLighting', (cfg, res) => {
  const a = (num(cfg.lightDir, 135) * Math.PI) / 180
  res.behind.push({ kind: 'extrude', depth: num(cfg.depth, 10), dx: Math.cos(a) * -1, dy: Math.sin(a) * -1, sideColor: cfg.material || '#444444', shading: 1, softness: num(cfg.roughness, 0) })
  res.over.push({ kind: 'bevel', clip: true, mode: 'inner', width: num(cfg.bevelWidth, 3), depth: 3, highlight: cfg.lightColor || '#ffffff', shadow: '#000000', softness: 2, dx: Math.cos(a) * 3, dy: Math.sin(a) * 3 })
})

// 78. Reflection & Refraction Composite
registerEffect('reflectionRefraction', (cfg, res, item, t, ctx, h) => {
  if (num(cfg.distortion, 0) > 0) { res.animated = true; res.filters.wave = { amplitude: num(cfg.distortion, 3), wavelength: 40, direction: 'vertical', phase: h.loopPhase(cfg, t, ctx, 0.4) * Math.PI * 2 } }
  res.over.push({ kind: 'reflection', clip: false, direction: 'down', distance: 2, opacity: num(cfg.reflOpacity, 0.4), fade: num(cfg.fade, 0.6), blur: num(cfg.reflBlur, 2), flip: true })
})

// ===========================================================================
// Main entry
// ===========================================================================

/**
 * Compute the combined TextEffectResult for an item's textEffects array.
 * @param {Array} effects - item.textEffects
 * @param {Object} item   - the text item
 * @param {number} t       - absolute time in seconds
 * @param {Object} ctx     - { width, height, fontSize, text, time:{start,duration} }
 */
export const computeTextEffects = (effects, item, t, ctx = {}) => {
  const res = emptyResult()
  if (!Array.isArray(effects) || effects.length === 0) return res
  for (const cfg of effects) {
    if (!cfg || cfg.enabled === false || !cfg.type) continue
    const fn = EFFECTS[cfg.type]
    if (!fn) continue
    try {
      fn(cfg, res, item, t, ctx, H)
    } catch (e) {
      // Never let one effect break the whole render.
      // eslint-disable-next-line no-console
      console.error('TextEffect error:', cfg.type, e)
    }
  }
  return res
}

// ===========================================================================
// DOM translator — convert a TextEffectResult into inline styles + layer specs
// the React text renderer can consume. Effects that the DOM cannot express are
// skipped here (the worker renders them); the preview stays close but not 1:1
// for mask-heavy effects.
// ===========================================================================

const gradientCss = (g) => {
  if (!g) return null
  const stops = [...(g.stops || [])].sort((a, b) => a.position - b.position)
    .map((s) => `${s.color} ${clamp01((s.position % 100 + 100) % 100 / 100) * 100}%`).join(', ')
  if (g.type === 'radial') {
    const cx = (num(g.centerX, 0.5) * 100).toFixed(1)
    const cy = (num(g.centerY, 0.5) * 100).toFixed(1)
    return `radial-gradient(circle at ${cx}% ${cy}%, ${stops})`
  }
  return `linear-gradient(${num(g.angle, 90)}deg, ${stops})`
}

/**
 * Build DOM styling from a TextEffectResult.
 * Returns:
 *   style      - inline style props to merge onto the text element's inner style
 *   textShadow - array of text-shadow strings (caller joins or merges)
 *   layers     - descriptors for extra DOM layers (reflection/overlays) the caller may render
 *   boxStyle   - style for a background box wrapper (or null)
 */
export const textEffectsToDomStyles = (res, item, ctx = {}) => {
  const style = {}
  const shadowParts = []
  const layers = []
  let boxStyle = null

  // --- Background box ---
  if (res.box) {
    const b = res.box
    boxStyle = {
      background: b.gradient ? gradientCss(b.gradient) : rgba(b.color, b.opacity),
      borderRadius: `${b.radius}px`,
      padding: `${b.padding}px`,
      ...(b.border > 0 ? { border: `${b.border}px solid ${b.borderColor}` } : {}),
    }
  }

  // --- Behind layers that CSS can express as shadows (drop/double/long/outer glow) ---
  for (const l of res.behind) {
    if (l.kind === 'shadow') {
      shadowParts.push(`${l.offsetX}px ${l.offsetY}px ${l.blur}px ${rgba(l.color, l.opacity)}`)
    } else if (l.kind === 'glow') {
      // Approximate glow as a few stacked shadows of increasing blur.
      shadowParts.push(`0 0 ${l.blur}px ${rgba(l.color, l.opacity)}`)
      shadowParts.push(`0 0 ${l.blur * 1.8}px ${rgba(l.color, l.opacity * 0.7)}`)
    } else if (l.kind === 'longshadow') {
      const a = (l.angle * Math.PI) / 180
      const steps = Math.max(1, Math.round(l.length))
      for (let i = 1; i <= steps; i++) {
        const op = l.mode === 'fade' ? l.opacity * (1 - i / steps) : l.opacity
        shadowParts.push(`${(Math.cos(a) * i).toFixed(1)}px ${(Math.sin(a) * i).toFixed(1)}px 0 ${rgba(l.color, op)}`)
      }
    } else if (l.kind === 'extrude') {
      const steps = Math.max(1, Math.round(l.depth))
      for (let i = 1; i <= steps; i++) {
        shadowParts.push(`${(l.dx * i).toFixed(1)}px ${(l.dy * i).toFixed(1)}px 0 ${rgba(l.sideColor, 1)}`)
      }
    }
  }

  // --- Inner shadow approximation (CSS can't inner-shadow text directly; use a subtle inset-like dark shadow) ---
  for (const l of res.over) {
    if (l.kind === 'innerShadow') {
      shadowParts.push(`${-l.offsetX}px ${-l.offsetY}px ${l.blur}px ${rgba(l.color, l.opacity * 0.5)}`)
    }
  }

  // --- Fill ---
  if (res.fill) {
    const f = res.fill
    if (f.mode === 'linear' || f.mode === 'radial') {
      const css = gradientCss(f.gradient)
      if (css) {
        style.backgroundImage = css
        style.WebkitBackgroundClip = 'text'
        style.backgroundClip = 'text'
        style.WebkitTextFillColor = 'transparent'
        style.color = 'transparent'
      }
    } else if (f.mode === 'solid') {
      style.color = f.color
      style.WebkitTextFillColor = f.color
      if (f.opacity != null && f.opacity < 1) style.opacity = f.opacity
    } else if (f.mode === 'duotone') {
      // Approximate: two-stop gradient shadow->highlight.
      const css = gradientCss({ type: 'linear', angle: 90, stops: [{ color: f.shadowColor, position: 0 }, { color: f.highlightColor, position: 100 }] })
      style.backgroundImage = css
      style.WebkitBackgroundClip = 'text'
      style.backgroundClip = 'text'
      style.WebkitTextFillColor = 'transparent'
      style.color = 'transparent'
    } else if (f.mode === 'image' && f.image) {
      style.backgroundImage = `url(${f.image})`
      style.backgroundSize = f.fit === 'contain' ? 'contain' : 'cover'
      style.WebkitBackgroundClip = 'text'
      style.backgroundClip = 'text'
      style.WebkitTextFillColor = 'transparent'
      style.color = 'transparent'
    }
    if (f.blend && f.blend !== 'normal') style.mixBlendMode = f.blend
  }

  // --- Strokes (CSS: paint-order + -webkit-text-stroke for the widest; extras as shadows) ---
  if (res.strokes.length) {
    const widest = [...res.strokes].sort((a, b) => b.width - a.width)[0]
    style.WebkitTextStrokeWidth = `${widest.width}px`
    style.WebkitTextStrokeColor = rgba(widest.color, widest.opacity)
    style.paintOrder = 'stroke fill'
    // Additional outlines approximated as blur-0 shadows in 8 directions.
    res.strokes.filter((s) => s !== widest).forEach((s) => {
      for (let a = 0; a < 360; a += 45) {
        const rad = (a * Math.PI) / 180
        shadowParts.push(`${(Math.cos(rad) * s.width).toFixed(1)}px ${(Math.sin(rad) * s.width).toFixed(1)}px 0 ${rgba(s.color, s.opacity)}`)
      }
    })
  }

  // --- Over layers CSS can do: edge/inner glow -> extra shadows; overlay color -> text-shadow tint is not possible, skip ---
  for (const l of res.over) {
    if (l.kind === 'innerGlow' || l.kind === 'edgeGlow') {
      shadowParts.push(`0 0 ${l.blur || 3}px ${rgba(l.color, (l.brightness || l.intensity || 1))}`)
    } else if (l.kind === 'reflection') {
      layers.push({ type: 'reflection', ...l })
    } else if (l.kind === 'overlay' && !l.clip) {
      layers.push({ type: 'overlay', ...l })
    } else if (['fire', 'smoke', 'particles', 'particleTrail', 'lightRays', 'lensFlare', 'lightning', 'sparkle'].includes(l.kind)) {
      layers.push({ type: l.kind, ...l })
    }
  }

  // --- Decorative lines ---
  if (res.lines.length) layers.push(...res.lines.map((l) => ({ type: 'line', ...l })))

  // --- Whole-layer filters CSS can express: blur, pixelate(approx via contrast), blend ---
  const filterParts = []
  if (res.filters.blur) filterParts.push(`blur(${res.filters.blur}px)`)
  if (res.filters.bgBlur) { /* bg blur handled by caller via backdrop-filter */ style.backdropFilter = `blur(${res.filters.bgBlur}px)` }
  if (res.filters.directionalBlur) filterParts.push(`blur(${Math.max(0, res.filters.directionalBlur.distance / 3)}px)`)
  if (filterParts.length) style.filter = filterParts.join(' ')
  if (res.filters.blendMode && res.filters.blendMode.mode !== 'normal') {
    style.mixBlendMode = res.filters.blendMode.mode
    if (res.filters.blendMode.opacity != null) style.opacity = res.filters.blendMode.opacity
  }

  if (shadowParts.length) style.textShadow = shadowParts.join(', ')

  return { style, textShadow: shadowParts, layers, boxStyle }
}

export default { computeTextEffects, textEffectsToDomStyles, isTextEffectType, textEffectTypes }
